import { useCallback } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';

import type { MetagamesUserEvent } from '../../../dto/metagames.types';
import { MetaGamesEventType } from '../../../dto/metagames.types';
import { useStores } from '../../../stores/useStores';
import { devError } from '../../../utils/log';
import { Loader } from '../../Loader/Loader';
import { Timer } from '../../Timer/Timer';

import classes from './Metagames.module.scss';

import './Metagames.scss';

export const MetagamesWidget = observer(({ event }: { event: MetagamesUserEvent }) => {
  const {
    metagamesStore,
    prizeDropStore,
    puzzleStore,
    hammersStore,
    coinTypeStore,
    trailStore,
    plinkoStore,
    rouletteStore,
  } = useStores();
  const { widgetImageUrl } = event;

  const eventByCoin = event[coinTypeStore.coinType.toLowerCase() as 'gc' | 'sc'];

  const onClickHandler = () => {
    if (!eventByCoin) {
      devError('MetagamesWidget onClickHandler error: eventByCoin is undefined');
      return;
    }

    const analyticsParams = {
      event_action: 'click',
      triggered_by: 'click',
      click_location: 'widget',
    } as const;

    let feature_sub_type: 'prizeDrop' | 'hammers' | 'puzzle' | 'trail' | 'plinko' | 'roulette';

    switch (event.typeName) {
      case MetaGamesEventType.HAMMERS:
        void hammersStore.startHammersGame();
        feature_sub_type = 'hammers';
        break;
      case MetaGamesEventType.PUZZLE:
        void puzzleStore.startPuzzleGame();
        feature_sub_type = 'puzzle';
        break;
      case MetaGamesEventType.PRIZE_DROP:
        void prizeDropStore.startPrizeDropGame({ type: 'info' });
        feature_sub_type = 'prizeDrop';
        break;
      case MetaGamesEventType.TRAIL:
        void trailStore.startTrailGame();
        feature_sub_type = 'trail';
        break;
      case MetaGamesEventType.PLINKO:
        void plinkoStore.startPlinkoGame();
        feature_sub_type = 'plinko';
        break;
      case MetaGamesEventType.ROULETTE:
        void rouletteStore.startRouletteGame();
        feature_sub_type = 'roulette';
        break;
      default:
        devError(`MetagamesWidget onClickHandler error: Unknown event type ${event.typeName}`);
        return;
    }

    void metagamesStore.sendAnalyticsEvent({ ...analyticsParams, feature_sub_type });
  };

  const onTimerEnd = useCallback(() => {
    void metagamesStore.fetch();
  }, [metagamesStore]);

  if (!eventByCoin) {
    return null;
  }

  const { widgetImageUrl: widgetImageByCoin } = eventByCoin;
  const imageUrl = widgetImageByCoin ? widgetImageByCoin : widgetImageUrl;

  const balance = eventByCoin.balance > 99 ? '99+' : eventByCoin.balance;
  const showBalance = eventByCoin.isCurrency;

  const isLoading = hammersStore.isLoadingGame || prizeDropStore.isLoadingGame || rouletteStore.isLoadingGame;

  return (
    <div className="home-widget">
      <div className={classNames(classes.metaGamesWidget, 'metaWidget', event.typeName)}>
        <div className={classNames(classes.widget, 'circle-parent')} onClick={onClickHandler}>
          <div className="circle" />
          <img alt="Widget" className={classes.icon} src={imageUrl} />
          {showBalance && (
            <>
              <div className={classes.count}>
                <span>{balance}</span>
              </div>
            </>
          )}
          <Timer
            className={classNames(classes.timer, 'widget-timer')}
            endDate={new Date(event.endDate)}
            onTimerEnd={onTimerEnd}
          />
        </div>
        {isLoading && <Loader cover fullScreen />}
      </div>
    </div>
  );
});
